import { invoiceApi } from "../api/InvoiceApi";
import {
  CreateInvoiceBillingReminderParams,
  CreateInvoiceQuoteParams,
  CreateInvoiceSaleParams,
  InvoiceBillingReminderFilters,
  InvoiceBillingReminderModel,
  InvoiceBillingReminderRenewResult,
  InvoiceBillingReminderSummaryModel,
  InvoiceCatalogItemModel,
  InvoiceCatalogPayload,
  InvoiceCategoryModel,
  InvoiceCustomerModel,
  InvoiceLyproCompanyModel,
  InvoicePaymentModel,
  InvoiceQuoteModel,
  InvoiceQuotePreviewResult,
  InvoiceSaleModel,
  InvoiceSalesFilters,
  InvoiceSalesSummaryModel,
  InvoiceSellerModel,
  InvoiceUsageQuoteModel,
  UpdateInvoiceBillingReminderParams,
} from "../models/Invoices/InvoiceModels";

class InvoiceManager {
  private static instance: InvoiceManager;

  public static get(): InvoiceManager {
    if (!this.instance) {
      this.instance = new InvoiceManager();
    }
    return this.instance;
  }

  async getCatalog(): Promise<InvoiceCatalogPayload | undefined> {
    const result = await invoiceApi.catalog.requestHard(null);
    return result.data;
  }

  async getLyproCompanies(): Promise<InvoiceLyproCompanyModel[]> {
    const result = await invoiceApi.lyproCompanies.requestHard(null);
    return result.data || [];
  }

  async getUsageQuote(params: {
    customerId: string;
    itemId: string;
    month?: string;
  }): Promise<InvoiceUsageQuoteModel | undefined> {
    const result = await invoiceApi.usageQuote.requestHard(params);
    return result.data;
  }

  async getQuotes(): Promise<InvoiceQuoteModel[]> {
    const result = await invoiceApi.quotes.requestHard(null);
    return result.data || [];
  }

  async previewQuote(
    payload: CreateInvoiceQuoteParams
  ): Promise<InvoiceQuotePreviewResult | undefined> {
    const result = await invoiceApi.previewQuote.requestHard(payload);
    return result.data;
  }

  async createQuote(
    payload: CreateInvoiceQuoteParams
  ): Promise<InvoiceQuoteModel | undefined> {
    const result = await invoiceApi.createQuote.requestHard(payload);
    return result.data;
  }

  async createCategory(
    payload: Partial<InvoiceCategoryModel>
  ): Promise<InvoiceCategoryModel | undefined> {
    const result = await invoiceApi.createCategory.requestHard(payload);
    return result.data;
  }

  async updateCategory(
    id: string,
    payload: Partial<InvoiceCategoryModel>
  ): Promise<InvoiceCategoryModel | undefined> {
    const result = await invoiceApi.updateCategory(id).requestHard(payload);
    return result.data;
  }

  async deleteCategory(id: string): Promise<boolean> {
    const result = await invoiceApi.deleteCategory(id).requestHard(null);
    return result.isSuccess;
  }

  async createItem(
    payload: Partial<InvoiceCatalogItemModel>
  ): Promise<InvoiceCatalogItemModel | undefined> {
    const result = await invoiceApi.createItem.requestHard(payload);
    return result.data;
  }

  async updateItem(
    id: string,
    payload: Partial<InvoiceCatalogItemModel>
  ): Promise<InvoiceCatalogItemModel | undefined> {
    const result = await invoiceApi.updateItem(id).requestHard(payload);
    return result.data;
  }

  async deleteItem(id: string): Promise<boolean> {
    const result = await invoiceApi.deleteItem(id).requestHard(null);
    return result.isSuccess;
  }

  async createCustomer(
    payload: Partial<InvoiceCustomerModel>
  ): Promise<InvoiceCustomerModel | undefined> {
    const result = await invoiceApi.createCustomer.requestHard(payload);
    return result.data;
  }

  async updateCustomer(
    id: string,
    payload: Partial<InvoiceCustomerModel>
  ): Promise<InvoiceCustomerModel | undefined> {
    const result = await invoiceApi.updateCustomer(id).requestHard(payload);
    return result.data;
  }

  async deleteCustomer(id: string): Promise<boolean> {
    const result = await invoiceApi.deleteCustomer(id).requestHard(null);
    return result.isSuccess;
  }

  async createSeller(
    payload: Partial<InvoiceSellerModel>
  ): Promise<InvoiceSellerModel | undefined> {
    const result = await invoiceApi.createSeller.requestHard(payload);
    return result.data;
  }

  async updateSeller(
    id: string,
    payload: Partial<InvoiceSellerModel>
  ): Promise<InvoiceSellerModel | undefined> {
    const result = await invoiceApi.updateSeller(id).requestHard(payload);
    return result.data;
  }

  async deleteSeller(id: string): Promise<boolean> {
    const result = await invoiceApi.deleteSeller(id).requestHard(null);
    return result.isSuccess;
  }

  async getBillingReminders(
    filters?: InvoiceBillingReminderFilters
  ): Promise<InvoiceBillingReminderModel[]> {
    const result = await invoiceApi.billingReminders.requestHard(
      filters || null
    );
    return result.data || [];
  }

  async getBillingReminderSummary(
    filters?: InvoiceBillingReminderFilters
  ): Promise<InvoiceBillingReminderSummaryModel | undefined> {
    const result = await invoiceApi.billingReminderSummary.requestHard(
      filters || null
    );
    return result.data;
  }

  async createBillingReminder(
    payload: CreateInvoiceBillingReminderParams
  ): Promise<InvoiceBillingReminderModel | undefined> {
    const result = await invoiceApi.createBillingReminder.requestHard(payload);
    return result.data;
  }

  async updateBillingReminder(
    id: string,
    payload: UpdateInvoiceBillingReminderParams
  ): Promise<InvoiceBillingReminderModel | undefined> {
    const result = await invoiceApi
      .updateBillingReminder(id)
      .requestHard(payload);
    return result.data;
  }

  async renewBillingReminder(
    id: string,
    payload?: CreateInvoiceSaleParams
  ): Promise<InvoiceBillingReminderRenewResult | undefined> {
    const result = await invoiceApi
      .renewBillingReminder(id)
      .requestHard(payload || null);
    return result.data;
  }

  async cancelBillingReminder(
    id: string,
    reason?: string
  ): Promise<InvoiceBillingReminderModel | undefined> {
    const result = await invoiceApi
      .cancelBillingReminder(id)
      .requestHard({ reason });
    return result.data;
  }

  async deleteBillingReminder(id: string): Promise<boolean> {
    const result = await invoiceApi.deleteBillingReminder(id).requestHard(null);
    return result.isSuccess;
  }

  async getSales(filters?: InvoiceSalesFilters): Promise<InvoiceSaleModel[]> {
    const result = await invoiceApi.sales.requestHard(filters || null);
    return result.data || [];
  }

  async getSalesSummary(
    filters?: InvoiceSalesFilters
  ): Promise<InvoiceSalesSummaryModel | undefined> {
    const result = await invoiceApi.salesSummary.requestHard(filters || null);
    return result.data;
  }

  async createSale(
    payload: CreateInvoiceSaleParams
  ): Promise<InvoiceSaleModel | undefined> {
    const result = await invoiceApi.createSale.requestHard(payload);
    return result.data;
  }

  async regenerateSalePdfs(
    saleId: string
  ): Promise<InvoiceSaleModel | undefined> {
    const result = await invoiceApi.regenerateSalePdfs(saleId).requestHard(null);
    return result.data;
  }

  async addPayment(
    saleId: string,
    payload: Partial<InvoicePaymentModel>
  ): Promise<InvoiceSaleModel | undefined> {
    const result = await invoiceApi.addPayment(saleId).requestHard(payload);
    return result.data;
  }

  async deletePayment(
    saleId: string,
    paymentId: string
  ): Promise<InvoiceSaleModel | undefined> {
    const result = await invoiceApi
      .deletePayment(saleId, paymentId)
      .requestHard(null);
    return result.data;
  }

  async voidSale(saleId: string): Promise<InvoiceSaleModel | undefined> {
    const result = await invoiceApi.voidSale(saleId).requestHard(null);
    return result.data;
  }
}

export const invoiceManager = InvoiceManager.get();
